import { cn } from "@/lib/utils";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import {
  ButtonHTMLAttributes,
  createContext,
  forwardRef,
  useContext,
} from "react";

/* ── Toolbar context ────────────────────────────────────────────────────── */

const ToolbarContext = createContext<false | "default" | "header">(false);

/** Wrap buttons inside a toolbar to suppress their standalone border/shadow and shrink to pill size */
export function ToolbarProvider({
  size = "default",
  children,
}: {
  size?: "default" | "header";
  children: React.ReactNode;
}) {
  return (
    <ToolbarContext.Provider value={size}>{children}</ToolbarContext.Provider>
  );
}

/* ── Button ─────────────────────────────────────────────────────────────── */

const buttonVariants = cva(
  "relative inline-flex items-center shrink-0 justify-center gap-1.5 text-sm font-medium transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        primary: "bg-accent text-on-accent hover:bg-accent-hover",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "text-ink hover:bg-border-light active:bg-border",
        danger:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border border-input bg-canvas text-foreground hover:bg-hover active:bg-active",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        sm: "h-7 px-2.5 text-xs rounded-full",
        md: "h-9 px-4 rounded-full",
        lg: "h-10 px-5 text-base rounded-full",
        icon: "h-9 w-9 rounded-full",
        // A header's round buttons: on phones as big as the iOS bar buttons
        // (2.5rem on the 17px phone root), the icon size on desktop like the Mac.
        header: "h-10 w-10 rounded-full md:h-9 md:w-9",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
    },
  },
);

const standaloneStyles = "border border-border";

/** Inside a toolbar pill: compact sizing to fit within the pill (36px, or a header-sized one) */
const toolbarOverrides = {
  default: "h-7.5 w-7.5 outline-none border-none",
  header: "h-8 w-8 md:h-7.5 md:w-7.5 outline-none border-none",
};

interface ButtonProps
  extends
    ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      loading,
      disabled,
      children,
      ...props
    },
    ref,
  ) => {
    const inToolbar = useContext(ToolbarContext);
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        ref={ref}
        disabled={disabled || loading}
        className={cn(
          buttonVariants({ variant, size }),
          inToolbar ? toolbarOverrides[inToolbar] : standaloneStyles,
          className,
        )}
        {...props}
      >
        {children}
      </Comp>
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
export type { ButtonProps };
